import type { ImgHTMLAttributes } from 'react'

type ImageProps = Pick<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'sizes'>
export type TemplateImageOptions = { fallbackWidth: number; widths: number[]; sizes: string }

// These sizes are generated by next-image-export-optimizer (next.config.js).
const generatedWidths = [16, 32, 48, 64, 96, 128, 256, 384, 640, 750, 828, 1080, 1200, 1920, 2048, 3840]
const generatedWidth = (width: number) => generatedWidths.find(value => value >= width) || 3840

const readManifest = (): Record<string, string> => {
  try {
    const value = JSON.parse(process.env.NEXT_PUBLIC_TEMPLATE_IMAGE_MANIFEST || '{}')
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
  } catch (_error) {
    return {}
  }
}
const manifest = readManifest()

const templatePath = (src: string) => {
  try {
    const host = process.env.NEXT_PUBLIC_HOST || 'template.invalid'
    const url = new URL(src, `https://${host}/`)
    if (!['http:', 'https:'].includes(url.protocol) || url.host !== host) return ''
    return decodeURIComponent(url.pathname)
  } catch (_error) {
    return ''
  }
}

export const getTemplateImageSrc = (src: string, width: number) => {
  const pathname = templatePath(src)
  const version = manifest[pathname]
  if (!/^\/images\/template\/.+\.(png|jpe?g|webp)$/i.test(pathname) || !/^[a-f0-9]{16}$/.test(version || '')) return ''
  const slash = pathname.lastIndexOf('/')
  const directory = pathname.slice(0, slash)
  const filename = pathname.slice(slash + 1).replace(/\.[^.]+$/, '')
  const optimizedPath = `${directory}/nextImageExportOptimizer/${filename}-opt-${generatedWidth(width)}.WEBP`
  // Optimizer copies these files into .next/static. That directory is retained
  // by both SSR and static deployments; the build container's public/ is not.
  return `/_next/static${optimizedPath.split('/').map(encodeURIComponent).join('/')}?v=${version}`
}

export const getTemplateImageProps = (src: string, options: TemplateImageOptions): ImageProps => {
  const optimizedSrc = getTemplateImageSrc(src, options.fallbackWidth)
  if (!optimizedSrc) return { src }
  const widths = [...new Set(options.widths.map(generatedWidth))].sort((a, b) => a - b)
  return {
    src: optimizedSrc,
    srcSet: widths.map(width => `${getTemplateImageSrc(src, width)} ${width}w`).join(', '),
    sizes: options.sizes,
  }
}
